import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Navigate, Link, useLocation } from "react-router-dom";
import FadeOutlet from "@/components/FadeOutlet";
import {
  LayoutGrid, PackagePlus, Bell, BellOff, BarChart3, Users, Settings, LogOut, Shield, ExternalLink, Star, Truck, History, FileText, RefreshCw, Building2, ScanLine, Menu, PackageCheck, SlidersHorizontal, Gift, Sparkles, Clock,
} from "lucide-react";
import Logo from "@/components/Logo";
import SupportSessionBanner from "@/components/SupportSessionBanner";
import ThemeToggle from "@/components/ThemeToggle";
import LanguageSelect from "@/components/LanguageSelect";
import EstablishmentSwitcher from "@/components/EstablishmentSwitcher";
import StaffOnboarding from "@/components/StaffOnboarding";
import ForcePasswordChange from "@/components/ForcePasswordChange";
import { EstablishmentProvider, useEstablishment } from "@/context/EstablishmentContext";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PageLoader } from "@/components/FindoSpinner";

const NAV = [
  { to: "/b2b/establishments", labelKey: "side.establishments", Icon: Building2, staff: false },
  { to: "/b2b/registry", labelKey: "side.registry", Icon: LayoutGrid },
  { to: "/b2b/storage", labelKey: "side.storage", Icon: ScanLine },
  { to: "/b2b/deposit", labelKey: "side.deposit", Icon: PackagePlus },
  { to: "/b2b/declarations", labelKey: "side.declarations", Icon: Bell, badgeKey: "unread" },
  { to: "/b2b/match-history", labelKey: "side.matchHistory", Icon: History, badgeKey: "matches" },
  { to: "/b2b/shipments", labelKey: "side.shipments", Icon: Truck },
  { to: "/b2b/restitutions", labelKey: "side.restitutions", Icon: PackageCheck },
  { to: "/b2b/reviews", labelKey: "side.reviews", Icon: Star },
  { to: "/b2b/rewards", labelKey: "side.rewards", Icon: Gift, badgeKey: "rewards" },
  { to: "/b2b/stats", labelKey: "side.stats", Icon: BarChart3 },
  { to: "/b2b/invoices", labelKey: "side.invoices", Icon: FileText, staff: false },
  { to: "/b2b/team", labelKey: "side.team", Icon: Users, staff: false },
  { to: "/b2b/settings", labelKey: "side.settings", Icon: SlidersHorizontal },
  { to: "/b2b/account", labelKey: "side.account", Icon: Settings, staff: false },
];

const TIER_KEY = { free: "tier.free", small: "tier.small", medium: "tier.medium", large: "tier.large" };

// Short pleasant chime via the Web Audio API (no asset needed).
function playChime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [880, 1320].forEach((freq, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = "sine"; o.frequency.value = freq;
      const t0 = ctx.currentTime + i * 0.14;
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.22, t0 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.32);
      o.start(t0); o.stop(t0 + 0.34);
    });
  } catch (e) { /* autoplay blocked before user gesture — ignore */ }
}

export default function DashboardLayout() {
  const { partner, loading, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [extPending, setExtPending] = useState(0);
  const [rewardsPending, setRewardsPending] = useState(0);
  const [matchAlerts, setMatchAlerts] = useState(0);
  const [notifPerm, setNotifPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [billingStatus, setBillingStatus] = useState(null);
  useEffect(() => {
    let alive = true;
    api("/monthly-invoices/billing-status")
      .then((s) => { if (alive) setBillingStatus(s); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (partner && partner.role && partner.role !== "owner" && partner.member_onboarded === false) {
      setShowOnboarding(true);
    }
  }, [partner]);

  function enableNotifications() {
    if (typeof Notification === "undefined") return;
    Notification.requestPermission().then((p) => {
      setNotifPerm(p);
      if (p === "granted") toast.success(t("dl.notifEnabled"));
    }).catch(() => {});
  }

  useEffect(() => {
    if (!partner) return;
    api("/declarations/unread-count").then((r) => setUnread(r?.count || 0)).catch(() => {});
    const fetchExt = () => api("/declarations/external-pending-count").then((r) => setExtPending(r?.count || 0)).catch(() => {});
    const fetchRewards = () => api("/partners/me/rewards-pending-count").then((r) => setRewardsPending(r?.count || 0)).catch(() => {});
    fetchExt();
    fetchRewards();
    const t = setInterval(() => {
      api("/declarations/unread-count").then((r) => setUnread(r?.count || 0)).catch(() => {});
      fetchExt();
      fetchRewards();
    }, 30000);
    window.addEventListener("findloop:ext-pending-changed", fetchExt);
    window.addEventListener("findloop:rewards-changed", fetchRewards);
    return () => { clearInterval(t); window.removeEventListener("findloop:ext-pending-changed", fetchExt); window.removeEventListener("findloop:rewards-changed", fetchRewards); };
  }, [partner]);

  // Real-time-ish visual-match alert: poll the AI match journal and ring a
  // chime + toast + badge when a NEW auto_matched event appears.
  useEffect(() => {
    if (!partner) return;
    const KEY = "findo_match_seen_at";
    async function poll() {
      try {
        const evs = await api("/matching/events?limit=10");
        const autos = (evs || []).filter((e) => e.decision === "auto_matched");
        if (!autos.length) return;
        const bookmark = localStorage.getItem(KEY);
        if (!bookmark) { localStorage.setItem(KEY, autos[0].created_at); return; }
        const fresh = autos.filter((e) => e.created_at > bookmark);
        if (fresh.length) {
          setMatchAlerts((c) => c + fresh.length);
          playChime();
          const first = fresh[0];
          const cname = first.customer_name || t("dl.aClient");
          toast.success(t("dl.matchToast").replace("{name}", cname), {
            description: first.reason || t("dl.matchToastDesc"),
          });
          // Real OS-level browser notification (works even when tab is in background).
          if (typeof Notification !== "undefined" && Notification.permission === "granted") {
            try {
              const n = new Notification(t("dl.matchNotifTitle"), {
                body: `${cname}${first.reason ? " · " + first.reason : ""}`,
                tag: "findo-match", renotify: true,
              });
              n.onclick = () => { window.focus(); navigate("/b2b/match-history"); };
            } catch (e) { /* ignore */ }
          }
          localStorage.setItem(KEY, autos[0].created_at);
        }
      } catch (e) { /* ignore */ }
    }
    poll();
    const id = setInterval(poll, 20000);
    return () => clearInterval(id);
  }, [partner, navigate, t]);

  if (loading) {
    return <PageLoader label={t("c.loading")} className="min-h-screen" />;
  }
  if (!partner) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth/login?next=${next}`} replace />;
  }

  const isSuperadmin = !!partner.is_superadmin || (partner.email || "").toLowerCase() === "admin@findo.app";
  const quotaPct = partner.quota_max ? Math.min(100, Math.round((partner.quota_used / partner.quota_max) * 100)) : 0;

  return (
    <EstablishmentProvider>
    <div className="min-h-screen flex internal-ui">
      {mobileNav && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setMobileNav(false)} data-testid="dashboard-nav-backdrop" />
      )}
      <aside className={`w-72 shrink-0 bg-white border-r border-border flex flex-col fixed h-screen z-50 transition-transform duration-300 lg:translate-x-0 ${mobileNav ? "translate-x-0" : "-translate-x-full"}`} data-testid="dashboard-sidebar">
        <div className="px-6 h-16 flex items-center justify-between border-b border-border">
          <Logo size={32} withText textClass="text-lg text-slate-900" />
          <div className="flex items-center gap-1.5">
            <PartnerNotificationBell t={t} />
            <LanguageSelect variant="compact" testid="b2b-language-select" />
            <ThemeToggle />
          </div>
        </div>

        <HeaderContextCard partner={partner} t={t} quotaPct={quotaPct} />

        {partner.role !== "staff" && <EstablishmentSwitcher />}

        <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
          {NAV.filter(({ staff }) => staff !== false || partner.role !== "staff").map(({ to, labelKey, Icon, badgeKey }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${to.split("/").pop()}`}
              onClick={() => { setMobileNav(false); if (badgeKey === "matches") setMatchAlerts(0); }}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                  isActive ? "bg-findo-indigo/10 text-findo-indigo" : "text-slate-600 hover:bg-slate-100"
                }`
              }
            >
              <Icon size={19} />
              <span className="flex-1">{t(labelKey)}</span>
              {badgeKey === "unread" && unread > 0 && (
                <span className="min-w-5 h-5 px-1.5 grid place-items-center rounded-full bg-red-500 text-white text-[11px] font-bold" data-testid="unread-badge">
                  {unread}
                </span>
              )}
              {to === "/b2b/declarations" && extPending > 0 && (
                <span className="min-w-5 h-5 px-1.5 grid place-items-center rounded-full bg-findo-indigo text-white text-[11px] font-bold" data-testid="ext-pending-badge" title={t("dl.extPending")}>
                  {extPending}
                </span>
              )}
              {badgeKey === "matches" && matchAlerts > 0 && (
                <span className="min-w-5 h-5 px-1.5 grid place-items-center rounded-full bg-findo-turquoise text-white text-[11px] font-bold animate-pulse" data-testid="match-alert-badge">
                  {matchAlerts}
                </span>
              )}
              {badgeKey === "rewards" && rewardsPending > 0 && (
                <span className="min-w-5 h-5 px-1.5 grid place-items-center rounded-full bg-red-500 text-white text-[11px] font-bold" data-testid="rewards-pending-badge" title={t("dl.rewardsPending")}>
                  {rewardsPending}
                </span>
              )}
            </NavLink>
          ))}
          {isSuperadmin && (
            <NavLink to="/superadmin" className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                isActive ? "bg-amber-100 text-amber-800" : "text-slate-600 hover:bg-slate-100"
              }`}>
              <Shield size={19} /> <span className="flex-1">Superadmin</span>
            </NavLink>
          )}
        </nav>

        <div className="p-3 border-t border-border space-y-1">
          {notifPerm === "default" && (
            <button
              data-testid="enable-notifications-btn"
              onClick={enableNotifications}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-findo-indigo bg-findo-indigo/10 hover:bg-findo-indigo/15"
            >
              <Bell size={19} /> {t("dl.enableAlerts")}
            </button>
          )}
          {notifPerm === "denied" && (
            <div data-testid="notifications-blocked-hint"
              className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5 text-xs text-amber-800">
              <p className="flex items-center gap-2 font-semibold"><BellOff size={15} /> {t("dl.alertsBlocked")}</p>
              <p className="mt-1 leading-snug">
                {t("dl.alertsBlockedHint")}
              </p>
            </div>
          )}
          <Link to="/" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">
            <ExternalLink size={19} /> {t("side.viewSite")}
          </Link>
          <button
            data-testid="logout-btn"
            onClick={() => { logout(); navigate("/"); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-red-600 hover:bg-red-50"
          >
            <LogOut size={19} /> {t("side.logout")}
          </button>
        </div>
      </aside>

      <main className="flex-1 lg:ml-72 min-h-screen w-full min-w-0">
        <div className="lg:hidden sticky top-0 z-30 bg-white border-b border-border h-14 flex items-center gap-3 px-4" data-testid="dashboard-mobile-topbar">
          <button onClick={() => setMobileNav(true)} className="h-10 w-10 grid place-items-center rounded-full border border-border text-slate-700 hover:bg-slate-50" aria-label="Menu" data-testid="dashboard-mobile-menu-btn">
            <Menu size={20} />
          </button>
          <Logo size={28} withText textClass="text-base text-slate-900" />
        </div>
        <SupportSessionBanner role="partner" />
        {partner.invoice_overdue && (
          <div className="bg-findo-danger text-white px-6 py-3" data-testid="overdue-banner">
            <div className="max-w-5xl mx-auto flex items-center justify-between gap-4 text-sm">
              <p className="font-semibold">
                ⚠ {t("dl.invoiceOverdue").replace("{num}", partner.overdue_invoice?.number || "")}
                {partner.overdue_invoice?.amount_eur != null && <> {t("dl.amountDue").replace("{amount}", partner.overdue_invoice.amount_eur).replace("{currency}", partner.overdue_invoice.currency || "EUR")}</>}
              </p>
              {partner.overdue_invoice?.link && (
                <a href={partner.overdue_invoice.link} target="_blank" rel="noreferrer"
                  className="rounded-full bg-white/20 hover:bg-white/30 px-4 py-1.5 font-semibold whitespace-nowrap transition-colors" data-testid="overdue-pay-link">
                  {t("dl.payInvoice")}
                </a>
              )}
            </div>
          </div>
        )}
        {billingStatus && billingStatus.days_until_suspension != null && partner.subscription_status !== "suspended" && (
          <div className="bg-amber-50 text-amber-900 border-b border-amber-200 px-6 py-3" data-testid="suspension-countdown-banner">
            <div className="max-w-5xl mx-auto flex items-center justify-between gap-4 text-sm">
              <p className="font-semibold">
                ⏳ {t("dl.suspensionCountdown")
                  .replace("{days}", billingStatus.days_until_suspension)
                  .replace("{num}", billingStatus.invoice_number || "")}
              </p>
              <button onClick={() => navigate("/b2b/invoices")}
                className="rounded-full bg-amber-500 text-white hover:bg-amber-600 px-4 py-1.5 font-semibold whitespace-nowrap transition-colors"
                data-testid="suspension-countdown-btn">
                {t("dl.payInvoice")}
              </button>
            </div>
          </div>
        )}
        {partner.subscription_status === "suspended" && (
          <div className="bg-findo-red text-white px-6 py-3" data-testid="suspended-banner">
            <div className="max-w-5xl mx-auto flex items-center justify-between gap-4 text-sm">
              <p className="font-semibold">⚠ {t("dl.accountSuspended")}</p>
              <button onClick={() => navigate("/b2b/invoices")}
                className="rounded-full bg-white text-findo-red hover:bg-white/90 px-4 py-1.5 font-semibold whitespace-nowrap transition-colors"
                data-testid="suspended-regularize-btn">
                {t("dl.regularize")}
              </button>
            </div>
          </div>
        )}
        {partner.tier === "free" && partner.quota_max ? (() => {
          const used = partner.quota_used || 0;
          const max = partner.quota_max;
          if (used < Math.floor(max * 0.8)) return null;
          const reached = used >= max;
          return (
            <div className={`px-6 py-3 ${reached ? "bg-findo-red text-white" : "bg-amber-50 text-amber-900 border-b border-amber-200"}`} data-testid="freemium-upgrade-banner">
              <div className="max-w-5xl mx-auto flex items-center justify-between gap-4 text-sm">
                <p className="font-semibold">
                  {reached
                    ? t("dl.freemiumReached").replace("{used}", used).replace("{max}", max)
                    : t("dl.freemiumNear").replace("{used}", used).replace("{max}", max)}
                </p>
                <button onClick={() => navigate("/b2b/account")}
                  className={`rounded-full px-4 py-1.5 font-semibold whitespace-nowrap transition-colors ${reached ? "bg-white/20 hover:bg-white/30" : "bg-amber-500 text-white hover:bg-amber-600"}`}
                  data-testid="freemium-upgrade-btn">
                  {t("dl.upgradeStarter")}
                </button>
              </div>
            </div>
          );
        })() : null}
        <FadeOutlet />
      </main>
      {showOnboarding && <StaffOnboarding onClose={() => setShowOnboarding(false)} />}
      <ForcePasswordChange />
    </div>
    </EstablishmentProvider>
  );
}

function HeaderContextCard({ partner, t, quotaPct }) {
  const { selected, isAll } = useEstablishment();
  const specific = !isAll && selected;
  const name = specific ? selected.name : partner.org_name;
  return (
    <div className="px-4 py-4">
      <div className="rounded-2xl findo-gradient p-4 text-white">
        <p className="text-xs opacity-80 font-medium">{t("side.partner")}</p>
        <p className="font-display font-bold truncate" data-testid="sidebar-context-name">{name}</p>
        <div className="mt-3 flex items-center justify-between text-xs">
          {specific ? (
            <span className="font-semibold bg-white/20 px-2 py-0.5 rounded-full" data-testid="sidebar-est-badge">
              {selected.is_primary ? t("est.primary") : t("est.secondary")}
            </span>
          ) : (
            <span className="font-semibold bg-white/20 px-2 py-0.5 rounded-full">{t(TIER_KEY[partner.tier]) || partner.tier}</span>
          )}
          <span className="opacity-90">{partner.quota_used}/{partner.quota_max >= 999999 ? "∞" : partner.quota_max}</span>
        </div>
        <div className="mt-2 h-1.5 rounded-full bg-white/25 overflow-hidden">
          <div className="h-full bg-white rounded-full transition-all" style={{ width: `${quotaPct}%` }} />
        </div>
      </div>
    </div>
  );
}

function PartnerNotificationBell({ t }) {
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { selectedId, isAll } = useEstablishment();
  const eq = !isAll && selectedId ? `?e=${encodeURIComponent(selectedId)}` : "";

  const load = () => {
    api(`/partners/me/notifications${eq}`).then(setData).catch(() => {});
  };
  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [eq]); // eslint-disable-line react-hooks/exhaustive-deps

  async function openChange(o) {
    setOpen(o);
    if (o && data.unread > 0) {
      try { await api(`/partners/me/notifications/read-all${eq}`, { method: "POST" }); load(); } catch { /* ignore */ }
    }
  }

  function goToItem(n) {
    const itemId = n?.meta?.item_id;
    if (!itemId) return;
    setOpen(false);
    navigate(`/b2b/registry?item=${itemId}`);
  }

  const items = data.notifications || [];
  return (
    <Popover open={open} onOpenChange={openChange}>
      <PopoverTrigger asChild>
        <button className="relative h-9 w-9 rounded-full border border-border grid place-items-center hover:bg-slate-100 transition-colors" data-testid="partner-notif-bell" aria-label="Notifications">
          <Bell size={17} className="text-slate-700" />
          {data.unread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[17px] h-[17px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center" data-testid="partner-notif-badge">
              {data.unread > 9 ? "9+" : data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0" data-testid="partner-notif-panel">
        <div className="px-4 py-3 border-b border-border flex items-center gap-2">
          <Bell size={15} className="text-findo-indigo" />
          <p className="font-semibold text-slate-900 text-sm">{t("dl.notifTitle")}</p>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-center text-slate-400 text-sm py-10" data-testid="partner-notif-empty">{t("dl.notifEmpty")}</p>
          ) : (
            items.map((n) => {
              const clickable = !!n?.meta?.item_id;
              const isMatch = n.type === "cross_match";
              const isLegal = n.type === "legal_alert";
              return (
              <div key={n.id}
                onClick={clickable ? () => goToItem(n) : undefined}
                className={`px-4 py-3 border-b border-border last:border-0 ${n.read ? "" : "bg-findo-indigo/[0.04]"} ${clickable ? "cursor-pointer hover:bg-slate-50 transition-colors" : ""}`}
                data-testid={`partner-notif-item-${n.id}`}>
                <div className="flex items-start gap-2.5">
                  <span className={`h-8 w-8 rounded-full grid place-items-center shrink-0 ${n.type === "payment_received" ? "bg-emerald-100 text-emerald-600" : isMatch ? "bg-fuchsia-100 text-fuchsia-600" : isLegal ? "bg-orange-100 text-orange-600" : "bg-findo-indigo/10 text-findo-indigo"}`}>
                    {n.type === "payment_received" ? <PackageCheck size={15} /> : isMatch ? <Sparkles size={15} /> : isLegal ? <Clock size={15} /> : <Bell size={15} />}
                  </span>
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 text-sm">{n.title}</p>
                    {n.body && <p className="text-xs text-slate-500 mt-0.5">{n.body}</p>}
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-[10px] text-slate-400">{n.created_at ? new Date(n.created_at).toLocaleString() : ""}</p>
                      {clickable && (
                        <span className={`text-[10px] font-semibold inline-flex items-center gap-0.5 ${isLegal ? "text-orange-600" : "text-fuchsia-600"}`} data-testid={`partner-notif-goto-${n.id}`}>
                          {isLegal ? t("hc.viewInRegistry") : t("hc.viewItem")} <ExternalLink size={10} />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
